import {
  Color,
  CylinderGeometry,
  Group,
  Mesh,
  MeshStandardNodeMaterial,
  SphereGeometry,
  Vector3,
  type BufferGeometry,
  type PerspectiveCamera,
  type Scene,
} from 'three/webgpu';
import {
  AnimState,
  NPC_BARK_RANGE,
  NPC_TALK_RANGE,
  PLAYER_HEIGHT,
  Race,
  clamp,
  packLook,
  type LookParts,
  type NpcDef,
  type Vec3,
} from '@stillsun/shared';
import type { CrowdField } from '../render/crowd.js';
import { registerWarmPool } from '../render/renderer.js';
import { releaseSlot } from './remote.js';
import './npcs.css';

/**
 * The people standing on the map.
 *
 * STILLSUN's first NPC is Isolde Vane at the Yard's Ferry, and everything about how she is
 * built here follows from one decision: **an NPC is map data, not an entity**. She is never in
 * a snapshot, the server spawns nothing for her, and this field reads `MapDef.npcs` directly.
 * The only thing that crosses the wire is the player's `InteractAction.Talk` and her id; the
 * server validates the reach against the same authored position this file draws her at.
 *
 * Three things make her read as a person rather than a marker:
 *
 *  - **A body in the crowd.** One slot in `render/crowd.ts`, standing in `AnimState.Idle`,
 *    written once at build and never touched again — she does not move, so her sixteen floats
 *    do not change. She costs no `Object3D`, no `SkinnedMesh` and no mixer, and she is drawn
 *    in the same instanced call as every citizen around her.
 *  - **A lantern.** Built from primitives (there is no lantern GLB): a post, a housing, and a
 *    flame that is the only lit thing about her. Cloned from one template, so N lanternkeepers
 *    are N matrices and one upload.
 *  - **A plate, not a waypoint.** `.npcplate` — moon on plum, Bebas, square, no glow — with a
 *    name *and a role*, fading out with distance. That second line is why an NPC keeps its own
 *    DOM plate instead of using the crowd's: the crowd's is a bare name, and a person you can
 *    talk to has to say what they are. Her crowd slot is deliberately left nameless so the two
 *    never draw at once.
 *
 * She barks once per deploy at `NPC_BARK_RANGE`, through whatever the host does with chat.
 */

/** DOM writes are the expensive part; the same 20 Hz the beacons and the quest ring use. */
const LABEL_INTERVAL = 1 / 20;
/** Plate anchor above her feet: clear of the head, below the lantern's reach. */
const PLATE_HEIGHT = PLAYER_HEIGHT + 0.42;
/** Opaque this close, gone by `PLATE_FADE_FAR`. Nothing else in the game fades a label by
 *  distance yet — objectives are meant to be legible from anywhere, a person is not. */
const PLATE_FADE_NEAR = 12;
const PLATE_FADE_FAR = 32;

/** Requiem: the flame is moon-white, the ironwork is plum. Nothing here is amber. */
const MOON = 0xf2effa;
const LILAC = 0xc9bcff;
const PLUM = 0x0e0a1c;

/** Lantern dimensions, in metres. It stands beside her at hip height, not held. */
const LANTERN_POST_H = 1.02;
const LANTERN_GLASS_H = 0.2;
/**
 * Where the lantern stands, in her own local frame. yaw 0 faces -Z, so local -Z is forward and
 * local +X is her right: half a metre off her right hand and a third of a metre in front of her,
 * which puts the only lit thing on her between her and whoever is walking up.
 */
const LANTERN_OFFSET = { x: 0.48, z: -0.3 };

/** Parked scale for the warm lantern, matching every other warm pool in the tree. */
const WARM_SCALE = 0.001;

/**
 * `NpcDef.body` -> a packed `look`.
 *
 * The shared contract calls this field "the client's `CharacterId`" and the four ids it names
 * are the four GLB bodies the old rig path downloaded. There are no GLB bodies any more: the
 * bake ships one human mesh, and a person is told apart by what they are wearing. So the four
 * authored ids become four wardrobes on the same body — which is what an NPC roster is anyway.
 *
 * An id the map invents that is not in this table draws as a bare Human rather than as nothing.
 */
const BODY_LOOK: Record<string, LookParts> = {
  'char-01': { race: Race.Human, calling: 2, head: 0, shoulders: 1, chest: 1 },
  'char-02': { race: Race.Human, calling: 1, head: 1, shoulders: 0, chest: 2 },
  'char-03': { race: Race.Human, calling: 4, head: 0, shoulders: 2, chest: 3 },
  'char-04': { race: Race.Human, calling: 3, head: 2, shoulders: 0, chest: 0 },
};
const BARE_HUMAN = packLook({ race: Race.Human });

function lookFor(body: string): number {
  const parts = BODY_LOOK[body];
  return parts ? packLook(parts) : BARE_HUMAN;
}

/** What the interact scan needs back. */
export interface NearNpc {
  id: number;
  name: string;
  dist: number;
  pos: Vec3;
}

interface Npc {
  def: NpcDef;
  /** The lantern's node. The *person* is `slot`, and has none. */
  group: Group;
  /** Crowd slot, or -1 when the field was full or never loaded. */
  slot: number;
  plate: HTMLDivElement;
  /** Plate anchor height, in metres above their feet — follows `bodyScale` so it clears the head. */
  plateY: number;
  /** Latched the first time the player comes within `NPC_BARK_RANGE`; cleared per deploy. */
  barked: boolean;
  lastOpacity: number;
}

export interface NpcHost {
  /** One line into the HUD chat, spoken by `speaker`. */
  onBark(speaker: string, line: string): void;
}

export class NpcField {
  private readonly scene: Scene;
  private readonly crowd: CrowdField | null;
  private readonly labelRoot: HTMLElement;
  private readonly host: NpcHost;
  private readonly root = new Group();
  private readonly npcs: Npc[] = [];
  /** Built once and cloned per NPC, so N lanterns are N matrices and one upload. */
  private lanternTemplate: Group | null = null;
  /** The parked lantern `warmBodies` registers, or `null` until it has run. */
  private warmPool: Group | null = null;
  private readonly owned: BufferGeometry[] = [];
  private readonly materials: MeshStandardNodeMaterial[] = [];
  private flameMat: MeshStandardNodeMaterial | null = null;
  private key = '';
  private clock = 0;
  private labelAcc = LABEL_INTERVAL;
  private readonly tmp = new Vector3();
  private readonly tmpView = new Vector3();
  /** Reused for every `crowd.setState` — a person who never moves must never allocate. */
  private readonly place: Vec3 = { x: 0, y: 0, z: 0 };

  /**
   * `crowd` is nullable for the same reason `RemoteManager`'s is: main.ts warns and carries on
   * when the bake is unreachable. The lantern and the plate still draw — she is still somewhere
   * to walk to, which was always the fail-soft contract here.
   */
  constructor(scene: Scene, crowd: CrowdField | null, labelRoot: HTMLElement, host: NpcHost) {
    this.scene = scene;
    this.crowd = crowd;
    this.labelRoot = labelRoot;
    this.host = host;
    this.root.name = 'npcs';
    scene.add(this.root);
  }

  /**
   * Put the map's people in the world. Idempotent for an unchanged set — a second Welcome on
   * the same map must not rebuild her or restart her bark latch mid-session.
   */
  setNpcs(list: readonly NpcDef[] | undefined): void {
    const defs = list ?? [];
    const key = defs
      .map((n) => `${String(n.id)}:${n.body}:${n.pos.x},${n.pos.z}:${n.yaw}`)
      .join('|');
    if (key === this.key) return;
    // `clear()` resets the key itself, so it has to run before this one is stamped — otherwise
    // every deploy would rebuild her and re-arm the greeting.
    this.clear();
    this.key = key;
    for (const def of defs) this.npcs.push(this.build(def));
  }

  /**
   * One person by `NpcDef.id`. The wire only ever carries an id — a quest hand-in included — so
   * this is how the HUD turns "return to 3" into a name and a place to put a marker.
   */
  byId(id: number): NearNpc | null {
    const npc = this.npcs.find((n) => n.def.id === id);
    return npc ? { id, name: npc.def.name, dist: 0, pos: npc.def.pos } : null;
  }

  /** Nearest person within `range`, for the host's interact scan. */
  nearest(from: Vec3, range = NPC_TALK_RANGE): NearNpc | null {
    let best: NearNpc | null = null;
    for (const npc of this.npcs) {
      const d = Math.hypot(from.x - npc.def.pos.x, from.z - npc.def.pos.z);
      if (d > range || (best && d >= best.dist)) continue;
      best = { id: npc.def.id, name: npc.def.name, dist: d, pos: npc.def.pos };
    }
    return best;
  }

  /**
   * `live` is "the player is actually in the world" — Welcome has landed and the loading plate
   * is down. The people are drawn either way (the deploy precompile wants them in the scene
   * early), but nobody speaks over a loading screen, and `viewer` before Welcome is still the
   * last session's predicted position.
   *
   * Nothing here touches a body any more: the crowd owns the clip clock, and an NPC's slot was
   * written once when she was built. This is the lantern breath, the bark and the plate.
   */
  update(dt: number, camera: PerspectiveCamera, viewer: Vec3, live: boolean): void {
    if (this.npcs.length === 0) return;
    this.clock += dt;

    // A lantern that has stood three hundred nights does not strobe: this is a breath, not a
    // pulse — ±12% at 0.2 Hz, one material shared by every lantern in the scene.
    if (this.flameMat)
      this.flameMat.emissiveIntensity = 3.4 * (1 + 0.12 * Math.sin(this.clock * 1.25));

    if (live) {
      for (const npc of this.npcs) {
        if (npc.barked) continue;
        const d = Math.hypot(viewer.x - npc.def.pos.x, viewer.z - npc.def.pos.z);
        if (d > NPC_BARK_RANGE) continue;
        npc.barked = true;
        this.host.onBark(npc.def.name, npc.def.bark);
      }
    }

    this.labelAcc += dt;
    if (this.labelAcc < LABEL_INTERVAL) return;
    this.labelAcc = 0;

    // One layout read for the whole set; each access forces a style recalc.
    const w = this.labelRoot.clientWidth;
    const h = this.labelRoot.clientHeight;
    if (w === 0 || h === 0) return;
    for (const npc of this.npcs) this.placePlate(npc, camera, viewer, w, h);
  }

  /**
   * Parks a lantern so the boot precompile builds its pipelines.
   *
   * There are no NPC *bodies* left to warm — `CrowdField.create()` builds every body and gear
   * mesh before that same precompile, which is what this used to be for — but the lantern is
   * still built lazily on the first `build()`, and `setNpcs` does not run until Welcome lands,
   * i.e. after the boot precompile. So the two glowing materials on it (the plum housing, which
   * is the only transparent thing an NPC has, and the moon-white flame) were compiling on the
   * first frame that drew a lanternkeeper. One clone of the template, parked at `WARM_SCALE`
   * with culling off, moves all three lantern pipelines behind the loading plate.
   *
   * `clone(true)` shares geometry *and* materials with the template, so this costs a handful of
   * transforms and no upload; `registerWarmPool` attaches it only for the duration of each
   * precompile (render/renderer.ts), so it is not in the frame's traversal in between. Awaited
   * by main.ts before `precompileScene`, which is why the signature stays async.
   */
  async warmBodies(): Promise<void> {
    await Promise.resolve();
    if (this.warmPool !== null) return;
    try {
      this.lanternTemplate ??= this.buildLantern();
      const pool = new Group();
      pool.name = 'npc-warm';
      pool.add(this.lanternTemplate.clone(true));
      pool.scale.setScalar(WARM_SCALE);
      // Per object, not on the Group: `compileAsync`'s `_projectObject` tests `frustumCulled` on
      // the mesh it is about to push and never on an ancestor. The Hymn pool learned this the
      // expensive way (WEBGPU-NOTES §6).
      pool.traverse((o) => {
        o.frustumCulled = false;
      });
      this.warmPool = pool;
      registerWarmPool(this.scene, pool);
    } catch (err) {
      console.warn('[stillsun] NPC lantern warm-up unavailable; it compiles on first draw', err);
    }
  }

  /** Tears the people down but leaves the field ready for another `setNpcs`. */
  clear(): void {
    for (const npc of this.npcs) {
      this.root.remove(npc.group);
      // Through `releaseSlot`, not `release`: an NPC with a `bodyScale` other than 1 would
      // otherwise hand the next acquirer their height.
      if (this.crowd) releaseSlot(this.crowd, npc.slot);
      npc.plate.remove();
    }
    this.npcs.length = 0;
    this.key = '';
  }

  dispose(): void {
    this.clear();
    for (const g of this.owned) g.dispose();
    this.owned.length = 0;
    for (const m of this.materials) m.dispose();
    this.materials.length = 0;
    this.flameMat = null;
    this.lanternTemplate = null;
    // `registerWarmPool` has no unregister, so the warm clone above stays on the renderer's list
    // holding materials this just disposed. Harmless as things stand — `dispose()` only runs on
    // teardown, after which nothing precompiles again — but it is the reason not to start
    // calling `dispose()` on a live session.
    this.warmPool = null;
    this.scene.remove(this.root);
  }

  // --- internals ---------------------------------------------------------------------

  private build(def: NpcDef): Npc {
    // The group carries the lantern only. yaw 0 faces -Z, which is three's own convention for
    // `rotation.y`, so the authored number goes on straight.
    const group = new Group();
    group.name = `npc:${String(def.id)}`;
    group.position.set(def.pos.x, def.pos.y, def.pos.z);
    group.rotation.y = def.yaw;

    this.lanternTemplate ??= this.buildLantern();
    // `clone` shares geometry and material with the template: a second lanternkeeper costs a
    // transform, not an upload.
    group.add(this.lanternTemplate.clone(true));
    this.root.add(group);

    const plate = this.buildPlate(def);
    this.labelRoot.appendChild(plate);

    const scale = def.bodyScale ?? 1;
    let slot = -1;
    if (this.crowd) {
      slot = this.crowd.acquire();
      if (slot >= 0) {
        this.crowd.setLook(slot, lookFor(def.body));
        this.crowd.setScale(slot, scale);
        // No crowd nameplate: this field draws its own, with the role line the crowd's has no
        // room for. Two plates over one head is the bug this line exists to prevent.
        this.crowd.setName(slot, null);
        this.place.x = def.pos.x;
        this.place.y = def.pos.y;
        this.place.z = def.pos.z;
        // Written once. She stands where the map put her, in the idle the field started her
        // clip clock at (`acquire()` desyncs it), and nothing moves either again.
        this.crowd.setState(slot, this.place, def.yaw, AnimState.Idle);
      }
    }

    return {
      def,
      group,
      slot,
      plate,
      plateY: PLATE_HEIGHT * scale,
      barked: false,
      lastOpacity: -1,
    };
  }

  /**
   * A lanternkeeper's lantern, from primitives — the repo has no lantern GLB. Four pieces: a
   * foot, a post, a plum housing, and one small moon-white sphere that is the only emissive
   * thing on her. Emissive rather than a real light: a `PointLight` here would cost a cluster
   * slot for a 5 cm flame nobody reads by.
   */
  private buildLantern(): Group {
    const iron = this.material(
      new MeshStandardNodeMaterial({
        color: 0x1b1630,
        roughness: 0.55,
        metalness: 0.35,
      }),
    );
    const glass = this.material(
      new MeshStandardNodeMaterial({
        color: PLUM,
        emissive: new Color(LILAC),
        emissiveIntensity: 0.6,
        roughness: 0.35,
        metalness: 0.1,
        transparent: true,
        opacity: 0.45,
      }),
    );
    const flame = this.material(
      new MeshStandardNodeMaterial({
        color: 0x2a2440,
        emissive: new Color(MOON),
        emissiveIntensity: 3.4,
        roughness: 1,
        metalness: 0,
      }),
    );
    this.flameMat = flame;

    const footGeo = this.geometry(new CylinderGeometry(0.055, 0.085, 0.04, 12));
    const postGeo = this.geometry(new CylinderGeometry(0.016, 0.02, LANTERN_POST_H, 8));
    const capGeo = this.geometry(new CylinderGeometry(0.085, 0.05, 0.07, 6));
    const glassGeo = this.geometry(new CylinderGeometry(0.072, 0.072, LANTERN_GLASS_H, 6, 1, true));
    const flameGeo = this.geometry(new SphereGeometry(0.045, 10, 8));

    const g = new Group();
    g.name = 'lantern';
    g.position.set(LANTERN_OFFSET.x, 0, LANTERN_OFFSET.z);

    const foot = new Mesh(footGeo, iron);
    foot.position.y = 0.02;
    const post = new Mesh(postGeo, iron);
    post.position.y = LANTERN_POST_H / 2; // cylinder origin is its centre
    const housing = new Mesh(glassGeo, glass);
    housing.position.y = LANTERN_POST_H + LANTERN_GLASS_H / 2;
    const cap = new Mesh(capGeo, iron);
    cap.position.y = LANTERN_POST_H + LANTERN_GLASS_H + 0.035;
    const wick = new Mesh(flameGeo, flame);
    wick.position.y = LANTERN_POST_H + LANTERN_GLASS_H * 0.45;

    for (const m of [foot, post, housing, cap, wick]) {
      m.castShadow = m !== wick && m !== housing;
      m.receiveShadow = false;
      g.add(m);
    }
    return g;
  }

  private buildPlate(def: NpcDef): HTMLDivElement {
    const plate = document.createElement('div');
    plate.className = 'npcplate off';
    const name = document.createElement('span');
    name.className = 'name';
    name.textContent = def.name;
    const role = document.createElement('span');
    role.className = 'role';
    role.textContent = `· ${def.role}`;
    plate.append(name, role);
    return plate;
  }

  private placePlate(
    npc: Npc,
    camera: PerspectiveCamera,
    viewer: Vec3,
    w: number,
    h: number,
  ): void {
    this.tmp.set(npc.def.pos.x, npc.def.pos.y + npc.plateY, npc.def.pos.z);
    // View space rather than an NDC z test: a point behind the eye divides by a negative w and
    // comes back mirrored (same reasoning as `render/beacons.ts`).
    this.tmpView.copy(this.tmp).applyMatrix4(camera.matrixWorldInverse);
    let off = this.tmpView.z >= -camera.near;
    if (!off) {
      this.tmp.project(camera);
      off = Math.abs(this.tmp.x) > 1.15 || Math.abs(this.tmp.y) > 1.15;
    }

    const d = Math.hypot(
      viewer.x - npc.def.pos.x,
      viewer.y - npc.def.pos.y,
      viewer.z - npc.def.pos.z,
    );
    const opacity = 1 - clamp((d - PLATE_FADE_NEAR) / (PLATE_FADE_FAR - PLATE_FADE_NEAR), 0, 1);
    if (off || opacity <= 0.01) {
      npc.plate.classList.add('off');
      return;
    }
    npc.plate.classList.remove('off');
    npc.plate.style.left = `${String(Math.round((this.tmp.x * 0.5 + 0.5) * w))}px`;
    npc.plate.style.top = `${String(Math.round((-this.tmp.y * 0.5 + 0.5) * h))}px`;
    // Rounded before the compare: a plate three metres away must not write `opacity` twenty
    // times a second for a change nobody can see.
    const q = Math.round(opacity * 20) / 20;
    if (q !== npc.lastOpacity) {
      npc.lastOpacity = q;
      npc.plate.style.opacity = String(q);
    }
  }

  private geometry<T extends BufferGeometry>(geo: T): T {
    this.owned.push(geo);
    return geo;
  }

  private material(mat: MeshStandardNodeMaterial): MeshStandardNodeMaterial {
    this.materials.push(mat);
    return mat;
  }
}
